import type { ReactNode } from 'react';
import { deltaClass, deltaLabel, deltaText, type Delta } from '../../lib/dayStats';

/**
 * One number, and how it moved.
 *
 * The delta column is fixed-width and right-aligned so the three of them stack
 * into a column rather than ragging against numbers of different lengths, and
 * `tabular-nums` throughout so a count ticking from 9 to 10 does not shove the
 * row sideways -- the same reason the checklist card's score has it.
 */
export function StatRow({
  icon,
  label,
  value,
  suffix,
  delta,
  vs,
  note,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  /** The "/6" on the wins row. Muted, and part of the number rather than a
   *  fourth column of its own. */
  suffix?: string;
  delta: Delta;
  /** "yesterday" or "last week", read out with the delta so the comparison is
   *  unambiguous even when the toggle has wrapped onto its own line. */
  vs: string;
  /** Why there is nothing to compare, when there is nothing to compare. */
  note?: string | null;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="flex min-w-0 items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-stone-100 text-stone-400">
          {icon}
        </span>
        <span className="truncate text-dense text-stone-600">{label}</span>
      </span>

      <span className="flex shrink-0 items-baseline gap-1.5">
        <span className="text-2xl font-semibold leading-none tabular-nums tracking-tight text-stone-900">
          {value}
        </span>
        {suffix ? <span className="text-sm tabular-nums text-stone-400">{suffix}</span> : null}

        <span
          title={note ?? undefined}
          className={`min-w-[2.75rem] pl-1 text-right text-xs font-medium tabular-nums ${deltaClass(delta)}`}
        >
          <span aria-hidden="true">{deltaText(delta)}</span>
          <span className="sr-only">{deltaLabel(delta, vs)}</span>
        </span>
      </span>
    </li>
  );
}
